import { TransferGroupTemplateEditContainerComponent } from '@app/containers/transfer-group-template/containers';
import { stringCaseInsensitiveComparator } from '@app/shared/common';
import { getEntityNameRequiredValidator } from '@app/shared/validators/entity-name-required.validator';
import {
  ACTIONS_PANEL_BUTTON,
  BTN_ADD_SITES,
  BTN_REMOVE,
} from '@app/text-content/button-templates';
import {
  FieldList,
  IFormGroupRules,
  IFormRules,
  ValidationService,
} from '@components/form/services';
import { WIDGET_FAMILY } from '@config/global-constants';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import {
  WIDGET_COMPONENT,
  IBaseFormService,
  ENTITY_TYPES,
  TRANSFER_TYPE_ENUM_OPTIONS,
  TRANSFER_TYPE,
  BUTTON_FLEX_ORDER,
  CELL_RENDERER,
  TOOLBAR_BUTTON_POSITION,
  DROPDOWN_MENU_POSITION,
  COLUMN_SELECTOR_ID,
} from '@shared/types';
import { isEmpty } from 'lodash';
import { MAX_GROUP_NAME_LENGTH_LIMIT } from '../transfer-group/transfer-group.constants';
import { getListGridRowCellClassForGroup } from '../transfer-group/utils/grid-utils';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

export const createFormRules = (
  formService: IBaseFormService<ENTITY_TYPES.TRANSFER_GROUP_TEMPLATE>,
  editor: TransferGroupTemplateEditContainerComponent,
): IFormRules => {
  const i18nService: XVII18nService = formService.i18nService;
  const sections: FieldList = {
    general_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: I18n.Common.General,
    },
    transfer_destinations_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: '',
    },
  };
  const panelGroups: IFormGroupRules[] = [
    {
      name: I18n.Common.General,
      fields: ['general_section', 'transfer_destinations_section'],
    },
    {
      id: 'general_section',
      fields: [
        'name',
        'transfer_type',
        'transfer_group_name',
        'site_id',
        'site_ids_to',
      ],
    },
    {
      id: 'transfer_destinations_section',
      fields: ['virtual.transfer_items'],
    },
  ];

  const topSectionFields: FieldList<string> = {
    // this is for duplicate name check entity id
    entity_id: {
      show: false,
    },
    name: {
      title: I18n.Forms.TransferForm.TemplateName,
      widgetFamily: WIDGET_FAMILY.XUX,
      widget: WIDGET_COMPONENT.input,
      required: true,
      widgetData: {
        placeholder: I18n.Forms.TransferForm.EnterTemplateName,
      },
      maxLength: MAX_GROUP_NAME_LENGTH_LIMIT,
      pattern: {
        value: ValidationService.PATTERNS.STRING_WITHOUT_SPECIAL_CHARACTERS,
        errorMessage: i18nService.translate(
          I18n.ErrorMessages.SpecialCharactersAreNotAllowed,
        ),
      },
      disabled: !editor.canEdit,
      validators: [
        getEntityNameRequiredValidator(
          ENTITY_TYPES.TRANSFER_GROUP_TEMPLATE,
          formService.i18nService,
        ),
      ],
      asyncValidators: [
        formService.transferGroupTemplateService.caseInsensitiveUniqueValidator(
          null,
          {
            include_active: true,
          },
        ),
      ],
    },
    transfer_type: {
      title: I18n.Common.Type,
      widget: WIDGET_COMPONENT.select,
      optionText: TRANSFER_TYPE_ENUM_OPTIONS,
      required: true,
      disabled: !editor.canEdit,
      defaultValue: TRANSFER_TYPE.PRE_EVENT_TRANSFER,
    },
    transfer_group_name: {
      title: I18n.Forms.TransferForm.TransferGroupName,
      widgetFamily: WIDGET_FAMILY.XUX,
      widget: WIDGET_COMPONENT.input,
      widgetData: {
        placeholder: I18n.Forms.TransferForm.EnterTransferGroupName,
      },
      maxLength: MAX_GROUP_NAME_LENGTH_LIMIT,
      pattern: {
        value: ValidationService.PATTERNS.STRING_WITHOUT_SPECIAL_CHARACTERS,
        errorMessage: i18nService.translate(
          I18n.ErrorMessages.SpecialCharactersAreNotAllowed,
        ),
      },
      required: false,
      disabled: !editor.canEdit,
    },
    site_id: {
      title: I18n.Common.From,
      widget: WIDGET_COMPONENT.entity_select,
      required: true,
      disabled: !editor.canEdit,
      widgetData: {
        entity: ENTITY_TYPES.SITE,
        dataSource: formService.siteIdDataSource,
        lazy: true,
        enableClientFiltering: true,
      },
      validators: [formService.getFromSiteRepeatValidator()],
    },
    site_ids_to: {
      show: false,
      required: false,
    },
  };

  const destinationSectionFields: FieldList<string> = {
    'virtual.transfer_items': {
      widget: WIDGET_COMPONENT.edit_grid_custom,
      title: I18n.EntityNames.TransferDestinations,
      class: 'no-padding',
      suppressNoPermissionsDisable: true,
      descriptionObs: formService.transferDestinationsDescription$,
      widgetData: {
        displayCountObs: formService.transferDestinationsDisplayCount$,
        selectedCountObs: formService.transferDestinationsSelectedCount$,
        useCustomButtonsOnFirstPosition: true,
        showSearch: true,
        multiselect: editor.canEdit,
        orderControls: false,
        disableColumnSelector: true,
        canRemove: editor.canEdit,
        showRowOptions: true,
        gridHeight: '600px',
        filterBeforeUpdateItems: true,
        showLoader: formService.listLoader$,
        userGridOptions: {
          overlayNoRowsTemplate: I18n.Common.NoRowsToShow,
          rowClassRules: getListGridRowCellClassForGroup(),
        },
        buttons: [
          {
            ...BTN_ADD_SITES,
            flexOrder: BUTTON_FLEX_ORDER.RIGHTMOST,
            position: TOOLBAR_BUTTON_POSITION.HEADER,
            dropdownMenuPosition: DROPDOWN_MENU_POSITION.RIGHT,
            disabled: !editor.canEdit,
            click: formService.openSiteSelector.bind(formService),
          },
          {
            ...BTN_REMOVE,
            position: TOOLBAR_BUTTON_POSITION.ROW,
          },
          {
            ...ACTIONS_PANEL_BUTTON,
            flexOrder: BUTTON_FLEX_ORDER.LEFTMOST,
            hidden: editor.canEdit ? isEmpty : true,
            childItems: [
              {
                ...BTN_REMOVE,
                class: 'xux-action-button btn full-width',
              },
            ],
          },
        ],
        columns: [
          {
            path: 'store_number',
            title: I18n.Sites.SiteId,
            sortable: true,
            comparator: stringCaseInsensitiveComparator,
            defaultView: true,
            filterParams: {
              quickFilter: true,
            },
          },
          {
            path: 'site_name_to',
            title: I18n.Sites.SiteName,
            sortable: true,
            defaultView: true,
            comparator: stringCaseInsensitiveComparator,
            filterParams: {
              quickFilter: true,
            },
          },
          {
            path: editor.canEdit ? COLUMN_SELECTOR_ID : 'none',
            width: 50,
            maxWidth: 50,
            title: '',
            defaultView: true,
            hide: !editor.canEdit,
            cellRenderer:
              CELL_RENDERER.transfer_group_template_actions_renderer,
            pinned: detectMobile() ? false : 'right',
            lockPinned: true,
          },
        ],
        composeConfirmationModalOptions:
          formService.composeConfirmationModalOptions.bind(formService),
      },
    },
  };

  return {
    defaultGroup: false,
    virtual: {
      type: 'object',
      properties: {
        transfer_items: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string' },
            },
          },
        },
      },
    },
    groups: [...panelGroups],
    fields: {
      ...sections,
      ...topSectionFields,
      ...destinationSectionFields,
    },
  };
};
